import { useEffect, useState } from 'react';
import { motion } from 'framer-motion';
import {
  Activity,
  AlertTriangle,
  BrainCircuit,
  Coins,
  Cpu,
  Gauge,
  LayoutDashboard,
  ListTree,
  RefreshCw,
  SlidersHorizontal,
  Tags,
} from 'lucide-react';
import {
  Area,
  AreaChart,
  ResponsiveContainer,
  Tooltip as RTooltip,
  XAxis,
} from 'recharts';
import { Button, Card, CardContent, Skeleton, cn, chartA11yProps } from '@respectly/ui';
import { useAiOpsStore, type CategoryFilter } from '../../stores/useAiOpsStore';
import { CATEGORY_META, COST_ACCENT, OPS_META, PUBLIC_META, TENANT_META, formatCompact, formatUsd } from './format';
import { UsageCostTab } from './UsageCostTab';
import { CallsTab } from './CallsTab';
import { AgentsTab } from './AgentsTab';
import { PricingTab } from './PricingTab';
import { BreakdownList } from './BreakdownList';

const container = { hidden: { opacity: 0 }, visible: { opacity: 1, transition: { staggerChildren: 0.06 } } };
const item = { hidden: { opacity: 0, y: 16 }, visible: { opacity: 1, y: 0, transition: { duration: 0.4 } } };

type TabKey = 'overview' | 'usage' | 'calls' | 'agents' | 'pricing';
const TABS: { key: TabKey; label: string; icon: typeof LayoutDashboard }[] = [
  { key: 'overview', label: 'Overview', icon: LayoutDashboard },
  { key: 'usage', label: 'Usage & Cost', icon: Activity },
  { key: 'calls', label: 'Calls', icon: ListTree },
  { key: 'agents', label: 'Agents & Models', icon: SlidersHorizontal },
  { key: 'pricing', label: 'Pricing', icon: Tags },
];

const RANGES: { label: string; days: number }[] = [
  { label: '7 days', days: 7 },
  { label: '30 days', days: 30 },
  { label: '90 days', days: 90 },
];

function isoDaysAgo(days: number): string {
  return new Date(Date.now() - days * 86400000).toISOString().slice(0, 10);
}

export function AiOperations() {
  const store = useAiOpsStore();
  const [tab, setTab] = useState<TabKey>('overview');
  const [rangeDays, setRangeDays] = useState(30);

  useEffect(() => {
    void store.loadOverview();
    void store.loadAgents();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (tab === 'calls' && store.calls.length === 0) void store.loadCalls(1);
    if (tab === 'pricing' && store.pricing.length === 0) void store.loadPricing();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [tab]);

  const refresh = () => {
    void store.loadOverview(store.timeseries?.groupBy ?? undefined);
    void store.loadAgents();
    if (tab === 'calls') void store.loadCalls(store.callsPage);
    if (tab === 'pricing') void store.loadPricing();
  };

  return (
    <motion.div variants={container} initial="hidden" animate="visible" className="space-y-6 p-1">
      {/* Header */}
      <motion.div variants={item} className="flex flex-wrap items-start justify-between gap-4">
        <div className="flex items-start gap-3">
          <div className="rounded-xl bg-gradient-to-br from-sky-500 to-violet-600 p-2.5 text-white shadow-lg shadow-sky-500/20">
            <BrainCircuit className="h-6 w-6" />
          </div>
          <div>
            <h1 className="text-2xl font-semibold tracking-tight text-slate-900 dark:text-slate-50">
              AI Operations
            </h1>
            <p className="text-sm text-slate-500 dark:text-slate-400">
              What AI costs to run — models, tokens, and spend across every agent.
            </p>
          </div>
        </div>
        <div className="flex items-center gap-2">
          <div className="flex rounded-lg border border-slate-200 bg-white p-0.5 dark:border-slate-700 dark:bg-slate-900">
            {RANGES.map((r) => (
              <button
                key={r.days}
                onClick={() => {
                  setRangeDays(r.days);
                  store.setRange(isoDaysAgo(r.days - 1), new Date().toISOString().slice(0, 10));
                }}
                className={cn(
                  'rounded-md px-3 py-1.5 text-xs font-medium transition-colors',
                  rangeDays === r.days
                    ? 'bg-slate-900 text-white dark:bg-slate-100 dark:text-slate-900'
                    : 'text-slate-600 hover:bg-slate-100 dark:text-slate-300 dark:hover:bg-slate-800'
                )}
              >
                {r.label}
              </button>
            ))}
          </div>
          <Button variant="outline" size="sm" onClick={refresh}>
            <RefreshCw className="mr-1.5 h-3.5 w-3.5" /> Refresh
          </Button>
        </div>
      </motion.div>

      {/* Category segmented control — view just Ops, Tenant-facing, or Website Chat */}
      <motion.div variants={item}>
        <CategorySegments value={store.category} onChange={store.setCategory} />
      </motion.div>

      {/* Burn-rate hero */}
      <motion.div variants={item}>
        <BurnRateHero />
      </motion.div>

      {/* Tab bar */}
      <motion.div variants={item} className="flex flex-wrap gap-1 border-b border-slate-200 dark:border-slate-800">
        {TABS.map((t) => {
          const Icon = t.icon;
          const active = tab === t.key;
          return (
            <button
              key={t.key}
              onClick={() => setTab(t.key)}
              className={cn(
                '-mb-px flex items-center gap-2 border-b-2 px-4 py-2.5 text-sm font-medium transition-colors',
                active
                  ? 'border-sky-500 text-sky-600 dark:text-sky-400'
                  : 'border-transparent text-slate-500 hover:text-slate-800 dark:text-slate-400 dark:hover:text-slate-200'
              )}
            >
              <Icon className="h-4 w-4" />
              {t.label}
            </button>
          );
        })}
      </motion.div>

      {store.error && (
        <div className="rounded-lg border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-700 dark:border-red-900/50 dark:bg-red-950/30 dark:text-red-300">
          {store.error}
        </div>
      )}

      <motion.div variants={item} className="min-h-[300px]">
        {tab === 'overview' && <OverviewTab />}
        {tab === 'usage' && <UsageCostTab />}
        {tab === 'calls' && <CallsTab />}
        {tab === 'agents' && <AgentsTab />}
        {tab === 'pricing' && <PricingTab />}
      </motion.div>
    </motion.div>
  );
}

// ─── Category segmented control ───────────────────────────────────────────────

function CategorySegments({ value, onChange }: { value: CategoryFilter; onChange: (c: CategoryFilter) => void }) {
  const opts: { key: CategoryFilter; label: string; color?: string }[] = [
    { key: 'all', label: 'All AI' },
    { key: 'ops', label: OPS_META.label, color: OPS_META.color },
    { key: 'tenant_facing', label: TENANT_META.label, color: TENANT_META.color },
    { key: 'public', label: PUBLIC_META.label, color: PUBLIC_META.color },
  ];
  return (
    <div className="inline-flex rounded-xl border border-slate-200 bg-white p-1 shadow-sm dark:border-slate-700 dark:bg-slate-900">
      {opts.map((o) => {
        const active = value === o.key;
        return (
          <button
            key={o.key}
            onClick={() => onChange(o.key)}
            className={cn(
              'flex items-center gap-2 rounded-lg px-4 py-2 text-sm font-medium transition-all',
              active
                ? 'bg-slate-900 text-white shadow dark:bg-slate-100 dark:text-slate-900'
                : 'text-slate-600 hover:bg-slate-100 dark:text-slate-300 dark:hover:bg-slate-800'
            )}
          >
            {o.color && <span className="h-2 w-2 rounded-full" style={{ background: o.color }} />}
            {o.label}
          </button>
        );
      })}
    </div>
  );
}

// ─── Burn-rate hero (the signature element) ──────────────────────────────────

function BurnRateHero() {
  const { summary, timeseries, loadingSummary, filters } = useAiOpsStore();

  if (loadingSummary && !summary) {
    return <Skeleton className="h-44 w-full rounded-2xl" />;
  }
  const totals = summary?.totals;
  const points = timeseries?.points ?? [];
  const cats = summary?.byCategory ?? [];
  const totalCost = totals?.costUsd ?? 0;

  return (
    <Card className="overflow-hidden border-slate-200 dark:border-slate-800">
      <CardContent className="p-0">
        <div className="grid gap-0 md:grid-cols-[1.1fr_1.4fr]">
          {/* Left: the spend readout */}
          <div className="relative bg-gradient-to-br from-slate-900 to-slate-800 p-6 text-white dark:from-slate-950 dark:to-slate-900">
            <div className="flex items-center gap-2 text-xs font-medium uppercase tracking-wider text-sky-300">
              <Coins className="h-4 w-4" /> Total AI spend · {filters.from} → {filters.to}
            </div>
            <div className="mt-3 font-mono text-5xl font-semibold tabular-nums tracking-tight">
              {formatUsd(totalCost)}
            </div>
            <div className="mt-1 text-sm text-slate-300">
              {formatCompact(totals?.totalTokens ?? 0)} tokens · {formatCompact(totals?.calls ?? 0)} calls
            </div>
            {/* Ops vs Tenant split bar */}
            <div className="mt-5">
              <div className="flex h-2.5 overflow-hidden rounded-full bg-white/10">
                {cats.map((c) => (
                  <div
                    key={c.key}
                    style={{ width: `${c.costShare}%`, background: CATEGORY_META[c.key]?.color ?? '#64748b' }}
                    title={`${c.label}: ${formatUsd(c.costUsd)}`}
                  />
                ))}
              </div>
              <div className="mt-2 flex flex-wrap gap-4 text-xs text-slate-300">
                {cats.map((c) => (
                  <span key={c.key} className="flex items-center gap-1.5">
                    <span className="h-2 w-2 rounded-full" style={{ background: CATEGORY_META[c.key]?.color }} />
                    {c.label} · {formatUsd(c.costUsd)} ({c.costShare}%)
                  </span>
                ))}
                {cats.length === 0 && <span>No spend recorded in this range yet.</span>}
              </div>
            </div>
          </div>

          {/* Right: cost sparkline */}
          <div className="p-4">
            <div className="mb-1 text-xs font-medium text-slate-500 dark:text-slate-400">Daily spend</div>
            {points.length === 0 ? (
              <div className="flex h-[150px] items-center justify-center text-sm text-slate-400">
                No AI calls in this range yet.
              </div>
            ) : (
              <ResponsiveContainer width="100%" height={150}>
                <AreaChart
                  data={points}
                  margin={{ top: 8, right: 8, left: 0, bottom: 0 }}
                  {...chartA11yProps(
                    `Daily AI spend from ${filters.from} to ${filters.to}: ${formatUsd(totalCost)} total across ${points.length} day${points.length === 1 ? '' : 's'}, peaking at ${formatUsd(Math.max(...points.map((p) => p.costUsd)))} in a single day.`
                  )}
                >
                  <defs>
                    <linearGradient id="spendGrad" x1="0" y1="0" x2="0" y2="1">
                      <stop offset="5%" stopColor={COST_ACCENT} stopOpacity={0.35} />
                      <stop offset="95%" stopColor={COST_ACCENT} stopOpacity={0} />
                    </linearGradient>
                  </defs>
                  <XAxis dataKey="date" tick={{ fontSize: 10, fill: '#94a3b8' }} tickLine={false} axisLine={false} minTickGap={24} />
                  <RTooltip
                    formatter={(v: number) => [formatUsd(v), 'Cost']}
                    contentStyle={{ fontSize: 12, borderRadius: 8, border: '1px solid #e2e8f0' }}
                  />
                  <Area type="monotone" dataKey="costUsd" stroke={COST_ACCENT} strokeWidth={2} fill="url(#spendGrad)" dot={false} />
                </AreaChart>
              </ResponsiveContainer>
            )}
          </div>
        </div>
      </CardContent>
    </Card>
  );
}

// ─── Overview tab ─────────────────────────────────────────────────────────────

function OverviewTab() {
  const { summary, loadingSummary } = useAiOpsStore();

  if (loadingSummary && !summary) {
    return (
      <div className="grid gap-4 md:grid-cols-4">
        {Array.from({ length: 4 }).map((_, i) => <Skeleton key={i} className="h-24 rounded-xl" />)}
      </div>
    );
  }
  const t = summary?.totals;

  return (
    <div className="space-y-6">
      <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-4">
        <StatTile icon={Coins} label="Total cost" value={formatUsd(t?.costUsd ?? 0)} accent="text-sky-600" />
        <StatTile icon={Cpu} label="Total tokens" value={formatCompact(t?.totalTokens ?? 0)} accent="text-violet-600" />
        <StatTile icon={Activity} label="Calls" value={formatCompact(t?.calls ?? 0)} accent="text-teal-600" />
        <StatTile
          icon={t && t.errorRate > 5 ? AlertTriangle : Gauge}
          label="Error rate"
          value={`${(t?.errorRate ?? 0).toFixed(1)}%`}
          accent={t && t.errorRate > 5 ? 'text-red-600' : 'text-emerald-600'}
        />
      </div>

      <div className="grid gap-4 lg:grid-cols-2">
        <BreakdownList title="Cost by feature" items={summary?.byFeature ?? []} />
        <BreakdownList title="Cost by agent" items={summary?.byAgent ?? []} />
        <BreakdownList title="Cost by model" items={summary?.byModel ?? []} />
        <BreakdownList title="Top tenants" items={summary?.topTenants ?? []} emptyHint="Internal/ops usage has no tenant." />
      </div>
    </div>
  );
}

function StatTile({
  icon: Icon,
  label,
  value,
  accent,
}: {
  icon: typeof Coins;
  label: string;
  value: string;
  accent: string;
}) {
  return (
    <Card className="border-slate-200 dark:border-slate-800">
      <CardContent className="flex items-center gap-4 p-5">
        <div className={cn('rounded-lg bg-slate-100 p-2.5 dark:bg-slate-800', accent)}>
          <Icon className="h-5 w-5" />
        </div>
        <div>
          <div className="text-xs font-medium uppercase tracking-wide text-slate-500 dark:text-slate-400">{label}</div>
          <div className="font-mono text-2xl font-semibold tabular-nums text-slate-900 dark:text-slate-50">{value}</div>
        </div>
      </CardContent>
    </Card>
  );
}
